import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Fingerprint, Lock, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { API, errText } from "@/app/session";
import { createPasskey, deviceName, getPasskey, isCancel, passkeyLabel, passkeysSupported } from "@/app/passkey";

const REMEMBER_KEY = "vortex_admin_remember";
const PASSKEY_HERE_KEY = "vortex_passkey_on_device";
const OFFER_DISMISSED_KEY = "vortex_passkey_offer_dismissed";

const getFlag = (k, fallback = false) => {
  try { const v = localStorage.getItem(k); return v === null ? fallback : v === "1"; } catch { return fallback; }
};
const setFlag = (k, on) => { try { localStorage.setItem(k, on ? "1" : "0"); } catch { /* storage blocked */ } };

const isMobile = () => typeof navigator !== "undefined" && /iPhone|iPad|Android/.test(navigator.userAgent);
const linkToken = () => {
  try { return new URLSearchParams(window.location.search).get("signin") || ""; } catch { return ""; }
};

// Face ID / passkey in one tap, a one-click link emailed to the owner inbox, or password then a 6-digit
// code emailed to the owner inbox. "Keep me
// signed in" makes the session last 30 days on this device. After a password sign-in on a device that
// can do passkeys, it offers to set one up so next time is one tap. On success the app opens on
// whatever page was requested.
const RememberBox = ({ remember, setRemember }) => (
  <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-300" data-testid="admin-remember">
    <Checkbox checked={remember} onCheckedChange={(v) => setRemember(v === true)}
      className="mt-0.5 border-white/30 data-[state=checked]:border-vortex-cyan data-[state=checked]:bg-vortex-cyan data-[state=checked]:text-vortex-ink" />
    <span>
      Keep me signed in on this device for 30 days
      <span className="block text-xs text-slate-500">Leave off on shared or public computers.</span>
    </span>
  </label>
);

const SignIn = ({ onSignedIn }) => {
  const canPasskey = passkeysSupported();
  const bio = passkeyLabel();
  const [step, setStep] = useState(() => (linkToken() ? "link" : "password"));
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(() => getFlag(REMEMBER_KEY, true));
  const [pending, setPending] = useState("");
  const [busy, setBusy] = useState(false);

  // Drop ?signin=... from the address bar so a used link isn't left in history or bookmarks.
  const clearLinkFromUrl = () => {
    if (!linkToken()) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("signin");
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  };

  const sendLink = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/email-link/send`);
      setStep("linksent");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const useLink = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/email-link/verify`, { token: linkToken(), remember });
      clearLinkFromUrl();
      finish(res.data.token);
    } catch (err) {
      toast.error(errText(err));
      clearLinkFromUrl();
      setStep("password");
      setBusy(false);
    }
  };

  const finish = (token) => {
    setFlag(REMEMBER_KEY, remember);
    onSignedIn(token, { remember });
    toast.success("Signed in.");
  };

  const passkeySignIn = async () => {
    setBusy(true);
    try {
      const { data: options } = await axios.post(`${API}/admin/passkeys/login/options`);
      const credential = await getPasskey(options);
      const res = await axios.post(`${API}/admin/passkeys/login`, { credential, remember });
      setFlag(PASSKEY_HERE_KEY, true);
      finish(res.data.token);
    } catch (err) {
      if (!isCancel(err)) toast.error(errText(err, `${bio} sign-in didn't work. Use your password instead.`));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (step === "password") {
        await axios.post(`${API}/admin/login`, { password });
        toast.success("Login code sent to the owner inbox.");
        setStep("code");
      } else {
        const res = await axios.post(`${API}/admin/verify`, { password, code, remember });
        setCode("");
        setPassword("");
        if (canPasskey && !getFlag(PASSKEY_HERE_KEY) && !getFlag(OFFER_DISMISSED_KEY)) {
          setPending(res.data.token);
          setStep("offer");
        } else {
          finish(res.data.token);
        }
      }
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const setUpPasskey = async () => {
    setBusy(true);
    const headers = { Authorization: `Bearer ${pending}` };
    try {
      const { data: options } = await axios.post(`${API}/admin/passkeys/register/options`, null, { headers });
      const credential = await createPasskey(options);
      await axios.post(`${API}/admin/passkeys/register`, { credential, name: deviceName() }, { headers });
      setFlag(PASSKEY_HERE_KEY, true);
      toast.success(`${bio} is set up. Next time, sign in with one tap.`);
      finish(pending);
    } catch (err) {
      if (!isCancel(err)) toast.error(errText(err, `Couldn't set up ${bio}. You can try again from Sign-in & Security.`));
      setBusy(false);
    }
  };

  const skipPasskey = () => {
    setFlag(OFFER_DISMISSED_KEY, true);
    finish(pending);
  };

  const field = "border-white/10 bg-vortex-ink/60 text-slate-100 focus-visible:ring-vortex-cyan";
  const label = "font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase";
  const primary = "flex w-full items-center justify-center gap-2 rounded-full bg-vortex-cyan px-6 py-4 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink transition-all duration-300 hover:shadow-[0_0_30px_rgba(92,179,226,0.4)] disabled:opacity-50";
  const secondary = "flex w-full items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-4 font-mono text-xs tracking-[0.2em] uppercase text-slate-200 transition-colors hover:border-vortex-cyan/60 hover:text-vortex-cyan disabled:opacity-50";

  const heading = {
    password: "Sign in", code: "Check your email", offer: `Use ${bio} next time?`,
    linksent: "Check your email", link: "Finish signing in",
  }[step];
  const intro = {
    password: canPasskey
      ? `Use ${bio} if it's set up on this device, or get a one-click sign-in link by email. No password needed for either.`
      : "Get a one-click sign-in link by email, or use the operator password plus an emailed code.",
    linksent: "We emailed a sign-in link to the owner inbox (info@vortexvs.com). Open it on this computer within 15 minutes. No password needed.",
    link: "Click below to sign in on this device.",
    code: "We emailed a 6-digit code to the owner inbox (info@vortexvs.com). Enter it within 10 minutes.",
    offer: `Set up ${bio} on this device and you can skip the password and email code from now on. It's as secure as both together, and only works on vortexvs.com.`,
  }[step];

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-16" data-testid="admin-login-page">
      <div className="w-full max-w-md border border-white/10 bg-vortex-surface/60 p-8 backdrop-blur-xl glow-cyan sm:p-10">
        <div className="flex items-center gap-3">
          <img src="/brand/logo-light.png" alt="Vortex Vending" className="h-8 w-auto" />
        </div>
        <div className="mt-8 flex items-center gap-2 text-vortex-cyan">
          <Lock size={16} />
          <p className="font-mono text-xs tracking-[0.25em] uppercase">Vortex Workspace</p>
        </div>
        <h1 className="mt-3 font-heading text-3xl font-black tracking-tighter text-white" data-testid="admin-login-heading">
          {heading}
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">{intro}</p>

        {step === "link" ? (
          <div className="mt-8 space-y-6">
            <button type="button" onClick={useLink} disabled={busy} className={primary} data-testid="email-link-confirm-button">
              {busy ? "Working..." : "Sign in"}
            </button>
            <RememberBox remember={remember} setRemember={setRemember} />
          </div>
        ) : step === "linksent" ? (
          <div className="mt-8 flex justify-between" data-testid="email-link-sent">
            <button type="button" onClick={sendLink} disabled={busy}
              className="font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase transition-colors hover:text-vortex-cyan">
              Send it again
            </button>
            <button type="button" onClick={() => setStep("password")}
              className="font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase transition-colors hover:text-vortex-cyan">
              Other ways to sign in
            </button>
          </div>
        ) : step === "offer" ? (
          <div className="mt-8 space-y-3">
            <button type="button" onClick={setUpPasskey} disabled={busy} className={primary} data-testid="passkey-setup-button">
              <Fingerprint size={16} /> {busy ? "Working..." : `Set up ${bio}`}
            </button>
            <button type="button" onClick={skipPasskey} disabled={busy} className={secondary} data-testid="passkey-skip-button">
              Not now
            </button>
          </div>
        ) : (
          <>
            {step === "password" && (
              <div className="mt-8 space-y-3">
                {canPasskey && (
                  <>
                    <button type="button" onClick={passkeySignIn} disabled={busy} className={primary} data-testid="passkey-signin-button">
                      <Fingerprint size={16} /> Sign in with {bio}
                    </button>
                    {!isMobile() && (
                      <p className="text-center text-xs text-slate-500" data-testid="phone-passkey-hint">
                        Or use your iPhone: click above, pick &ldquo;Use a phone or tablet&rdquo;, and scan the code with your phone&apos;s camera.
                      </p>
                    )}
                  </>
                )}
                <button type="button" onClick={sendLink} disabled={busy} className={canPasskey ? secondary : primary}
                  data-testid="email-link-send-button">
                  <Mail size={16} /> Email me a sign-in link
                </button>
                <div className="flex items-center pt-3 gap-3 font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase">
                  <span className="h-px flex-1 bg-white/10" /> or use your password <span className="h-px flex-1 bg-white/10" />
                </div>
              </div>
            )}
            <form onSubmit={submit} className={`${step === "password" ? "mt-6" : "mt-8"} space-y-5`} data-testid="admin-login-form">
              <div className="space-y-2">
                <Label htmlFor="op-password" className={label}>Password</Label>
                <Input id="op-password" data-testid="admin-password-input" type="password" required value={password}
                  autoComplete="current-password" onChange={(e) => setPassword(e.target.value)} className={field}
                  placeholder="Operator password" />
              </div>
              {step === "code" && (
                <div className="space-y-2">
                  <Label htmlFor="op-code" className={label}>One-time code</Label>
                  <Input id="op-code" data-testid="admin-code-input" required maxLength={6} inputMode="numeric"
                    autoComplete="one-time-code" autoFocus value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className={`${field} text-center font-mono text-lg tracking-[0.5em]`} placeholder="000000" />
                </div>
              )}
              <button type="submit" disabled={busy} data-testid="admin-login-submit-button"
                className={step === "password" ? secondary : primary}>
                {busy ? "Working..." : step === "password" ? "Send login code" : "Verify & sign in"}
              </button>
            </form>
            <div className="mt-6"><RememberBox remember={remember} setRemember={setRemember} /></div>
            {step === "code" && (
              <button data-testid="admin-login-back-button" onClick={() => { setStep("password"); setCode(""); }}
                className="mt-5 font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase transition-colors hover:text-vortex-cyan">
                Back to resend the code
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SignIn;
